import clsx from "clsx";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { AssetGroup, SymbolOverride } from "../data";
import type { OverrideParty } from "./helpers";

import { Button as ButtonV2 } from "../../../Button";
import { ChevronLeftIcon, CrossIcon, EditIcon, PlusIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { DiscardConfirmOverlay } from "../CreateProgramWizard/DiscardConfirmOverlay";
import { WizardFooter } from "../CreateProgramWizard/WizardFooter";
import { buildAssetClassOfSymbol } from "./catalogue";
import { findShadowedAssetClass, OVERRIDE_RULE_CAP } from "./helpers";
import { RuleEditor } from "./RuleEditor";
import { ShadowNote, SplitLegend } from "./SplitLegend";

export { OverrideEntry } from "./OverrideEntry";
export {
  formatOverridePcts,
  OVERRIDE_RULE_CAP,
  PARTY_COLOR_CLASSES,
} from "./helpers";
export type { OverrideParty } from "./helpers";

const RuleCard = ({
  rule,
  rules,
  parties,
  onEdit,
  onRemove,
  lookupAssetClass,
}: {
  rule: SymbolOverride;
  rules: SymbolOverride[];
  parties: OverrideParty[];
  onEdit: () => void;
  onRemove: () => void;
  lookupAssetClass: (symbol: string) => string | null;
}) => {
  const { t } = useTranslation();
  const shadowedAssetClass = findShadowedAssetClass(
    rule.scopeType === "symbol" ? rule.scopeValue : null,
    rules,
    lookupAssetClass,
  );

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-stroke-primary p-4">
      <div className="flex items-start justify-between gap-4">
        <span className="flex min-w-0 flex-wrap items-baseline gap-2">
          <Typo element="span" type="labelSM" textColor="text-text-primary">
            {rule.scopeValue}
          </Typo>
          <Typo element="span" type="bodySM" textColor="text-text-secondary">
            {rule.scopeType === "symbol"
              ? t("subIBReport_overrideScopeSymbol", "Symbol")
              : t("subIBReport_overrideScopeAssetClass", "Asset class")}
          </Typo>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            aria-label={t(
              "subIBReport_overrideEditFor",
              "Edit override for {{scope}}",
              {
                scope: rule.scopeValue,
              },
            )}
            className="flex h-6 w-6 items-center justify-center rounded text-text-brand-default hover:bg-overlay-opacity-state-hover"
          >
            <EditIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={t(
              "subIBReport_overrideRemoveFor",
              "Remove override for {{scope}}",
              { scope: rule.scopeValue },
            )}
            className="flex h-6 w-6 items-center justify-center rounded text-icon-secondary hover:bg-overlay-opacity-state-hover"
          >
            <CrossIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      </div>
      <SplitLegend rule={rule} parties={parties} />
      {shadowedAssetClass && <ShadowNote assetClass={shadowedAssetClass} />}
    </div>
  );
};

/**
 * Rebate split override sub-page — rendered *instead of* the wizard's step
 * content, with its own Back/close chrome, so it reads as a sub-page rather than
 * a nested modal.
 *
 * Edits are staged in local state and only handed back on Apply, which is what
 * makes Cancel a true discard. `initialView` lets a caller jump straight to the
 * editor (the empty-state path) rather than showing an empty list first.
 */
export const RebateSplitOverride = ({
  parties,
  rules,
  onApply,
  onCancel,
  onDirtyChange,
  initialView = "list",
  assets,
  isMobile,
  footerContainer,
}: {
  parties: OverrideParty[];
  rules: SymbolOverride[];
  onApply: (rules: SymbolOverride[]) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  initialView?: "list" | "editor";
  assets?: AssetGroup[] | null;
  isMobile?: boolean;
  footerContainer?: Element | null;
}) => {
  const { t } = useTranslation();
  const lookupAssetClass = buildAssetClassOfSymbol(assets);

  const [staged, setStaged] = useState<SymbolOverride[]>(() =>
    rules.map((rule) => ({ ...rule })),
  );
  const [view, setView] = useState<"list" | "editor">(initialView);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const atCap = staged.length >= OVERRIDE_RULE_CAP;

  // Separate from the sub-page's own `dirty` (staged/applied rules): this tracks
  // whether the *in-progress* Add/Edit form has unsaved field changes, so Back/
  // Cancel/Close on the editor warn even for a rule that was never Saved and so
  // never touched `staged`.
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorDiscardAction, setEditorDiscardAction] = useState<
    "back" | "close" | null
  >(null);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const openNew = () => {
    setEditIndex(null);
    setView("editor");
  };
  const openEdit = (index: number) => {
    setEditIndex(index);
    setView("editor");
  };
  const saveRule = (rule: SymbolOverride) => {
    setStaged((prev) =>
      editIndex === null
        ? [...prev, rule]
        : prev.map((existing, i) => (i === editIndex ? rule : existing)),
    );
    setDirty(true);
    setView("list");
  };

  const requestBackFromEditor = () => {
    if (editorDirty) {
      setEditorDiscardAction("back");
      return;
    }
    setView("list");
  };
  const requestCloseFromEditor = () => {
    if (editorDirty) {
      setEditorDiscardAction("close");
      return;
    }
    onCancel();
  };

  // Matches the wizard's own stepper+close row (CreateProgramWizard/index.tsx):
  // pinned to the top of the same scroll container, `z-[1]` to stay under
  // DiscardConfirmOverlay's `z-[2]`, `pt-5` on mobile to replace the vertical
  // padding the mobile shell no longer carries, and the spacing below it as
  // `pb-6` rather than a margin — a margin would leave a transparent strip
  // under the pinned row for content to scroll into, and 24px is what the
  // wizard header uses (this was `mb-8`).
  const header = (
    <div
      className={clsx(
        "sticky top-0 z-[1] flex items-center justify-between gap-4 bg-surface-primary pb-6",
        isMobile && "pt-5",
      )}
    >
      <button
        type="button"
        onClick={view === "editor" ? requestBackFromEditor : onCancel}
        className="group inline-flex items-center gap-1 text-text-secondary hover:text-text-primary"
      >
        <ChevronLeftIcon
          className="h-4 w-4 [&_path]:stroke-icon-tertiary group-hover:[&_path]:stroke-icon-primary"
          aria-hidden="true"
        />
        <Typo element="span" type="labelSM" textColor="text-text-secondary">
          {t("common_back", "Back")}
        </Typo>
      </button>
      <button
        type="button"
        onClick={view === "editor" ? requestCloseFromEditor : onCancel}
        aria-label={t("common_close", "Close")}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
      >
        <CrossIcon className="h-6 w-6" aria-hidden="true" />
      </button>
    </div>
  );

  if (view === "editor") {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        {header}
        <RuleEditor
          parties={parties}
          initial={editIndex === null ? null : staged[editIndex]}
          existingRules={staged}
          editIndex={editIndex}
          onCancel={requestBackFromEditor}
          onSave={saveRule}
          onDirtyChange={setEditorDirty}
          assets={assets}
          isMobile={isMobile}
          footerContainer={footerContainer}
        />
        {editorDiscardAction && (
          <DiscardConfirmOverlay
            onStay={() => setEditorDiscardAction(null)}
            onDiscard={() => {
              const action = editorDiscardAction;
              setEditorDiscardAction(null);
              if (action === "back") setView("list");
              else onCancel();
            }}
            isMobile={isMobile}
          />
        )}
      </div>
    );
  }

  const addRuleButton = (
    <ButtonV2 variant="tonal" size="md" onClick={openNew}>
      <div className="flex items-center gap-2">
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("subIBReport_addRule", "Add rule")}
      </div>
    </ButtonV2>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header}

      <div className="mb-4 flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <Typo
            element="h2"
            type={isMobile ? "headingLG" : "headingXL"}
            textColor="text-text-primary"
          >
            {t("subIBReport_overridePageTitle", "Set override rule")}
          </Typo>
          <Typo element="p" type="bodySM" textColor="text-text-secondary">
            {t(
              "subIBReport_overrideRulesDescription",
              "Create a specific rebate split rule to override the default settings for specific symbols or asset classes. Anything not listed uses your default split.",
            )}
          </Typo>
        </div>

        {staged.length === 0 ? (
          <div className="flex justify-center rounded-lg border border-dashed border-text-disabled p-4">
            {addRuleButton}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {staged.map((rule, index) => (
              <RuleCard
                key={`${rule.scopeType}-${rule.scopeValue}`}
                rule={rule}
                rules={staged}
                parties={parties}
                onEdit={() => openEdit(index)}
                onRemove={() => {
                  setStaged((prev) => prev.filter((_, i) => i !== index));
                  setDirty(true);
                }}
                lookupAssetClass={lookupAssetClass}
              />
            ))}
            {atCap ? (
              <Typo element="p" type="bodyXS" textColor="text-text-secondary">
                {t(
                  "subIBReport_overrideCapReached",
                  "You've reached the maximum number of rules",
                )}
              </Typo>
            ) : (
              <div className="flex justify-center rounded-lg border border-dashed border-text-disabled p-4">
                {addRuleButton}
              </div>
            )}
          </div>
        )}
      </div>

      {/* TODO: remove IIFE per "No IIFEs in JSX" (AGENTS.md) — pick basic/medium/complex tier */}
      {(() => {
        const footer = (
          <WizardFooter isMobile={isMobile}>
            <ButtonV2 variant="tonal" size="md" onClick={onCancel}>
              {t("subIBReport_cancel", "Cancel")}
            </ButtonV2>
            <ButtonV2 variant="brand" size="md" onClick={() => onApply(staged)}>
              {t("subIBReport_apply", "Apply")}
            </ButtonV2>
          </WizardFooter>
        );
        return footerContainer ? createPortal(footer, footerContainer) : footer;
      })()}
    </div>
  );
};
